import _merge from 'lodash/merge';
import { Lightning, Router } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { pairing } from '@bedrock-rdk/config';
import { clearPairingToken, fetchToken, getCurrentPairedUser, getStatus } from '../../../logic/pairing/pairing.actions';
import { disableProfileAutoSelect } from '../../../logic/lock/lock.actions';
import { PAIRING_STATUS_PAIRED } from '../../../logic/pairing/pairing.constants';
import { sendEvent } from '../../../logic/analytics/analytics.utils';
import { removeSplashScreen } from '../../../logic/app/app.utils';
import {
  getStatus as getSingleSignInStatus,
  exchangePlatformToken,
} from '../../../logic/pairing/customer/default/pairingProvider.actions';
import { clearCache, loadNavigation } from '../../../logic/layout/layout.actions';
import { showPopupAction } from '../../../logic/popup/popup.actions';
import { FATAL_ERROR_POPUP_ID } from '../../popups/popups.constants';
import { navigateTo, HOME_PATH } from '../../routes/routes.utils';
import { SELECTED_SIGNAL } from '../../tornado/signals';
import Spinner from '../../tornado/spinner/spinner.component';
import { PairingSelector } from '../../tornado/pairing/selector/pairingSelector.component';
import { BrandIcon } from '../../tornado/brand-icon/brandIcon.component';
import { DesktopPairing } from '../../tornado/pairing/desktop/desktopPairing.component';
import { MobilePairing } from '../../tornado/pairing/mobile/mobilePairing.component';
import { t } from '../../i18n';
import { SSIPairing } from './ssi/ssi.component';
import { hasSSISupport, setPairingMethod } from '../pairing.utils';
import { PAIRING_METHOD } from '../pairing.constants';
import { getInitialStyle, defaultContext } from './pairing.style';
import connect from './pairing.connector';

/**
 * Pairing page
 *
 * @version 1.2.1
 * @see https://zeroheight.com/726d5c282/p/00d378-pairing-page--v121/b/83ab7b
 */
export class Pairing extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        CustomerIcon: {
          type: BrandIcon,
        },
        Left: {
          type: PairingSelector,
          labels: {
            title: t('pairing.token.connect'),
          },
          signals: {
            [SELECTED_SIGNAL]: '_switchViews',
          },
        },
        Right: {
          SSI: {
            type: SSIPairing,
            signals: {
              accept: '_handleAccept',
            },
          },
          Mobile: {
            type: MobilePairing,
            labels: {
              title: t('pairing.token.synchRedirection'),
              steps: 'pairing.token.mobile.steps.step',
              stepsCount: 2,
            },
          },
          Desktop: {
            type: DesktopPairing,
            labels: {
              title: t('pairing.token.synchRedirection'),
              steps: 'pairing.token.computer.steps.step',
              stepsCount: 3,
            },
          },
        },
        Spinner: {
          type: Spinner,
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Left extends this {
        _getFocused() {
          return this.tag('Left');
        }

        _handleRight() {
          if (hasSSISupport() && this.tag('SSI').visible) {
            this._setState('Right');

            return true;
          }

          return false;
        }
      },
      class Right extends this {
        _getFocused() {
          return this._views.map(id => this.tag(id)).find(view => view.visible);
        }

        _handleLeft() {
          if (this._ssiCompletion) {
            return false;
          }

          this._setState('Left');

          return true;
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
    this._focusedButton = 0;
    this._pairingIntervalId = null;
    this._tokenTimeout = null;
    this._pollingInterval = 10000;
    this._ssiCompletion = false;
  }

  _build() {
    this.tag('Left').visible = false;
    this.tag('Right').visible = false;
    this.tag('Spinner').visible = false;
  }

  async _active() {
    removeSplashScreen();

    this._ssiCompletion = false;
    this._unsubscribe = connect(this);

    this.tag('Spinner').visible = true;
    // Single sign-in
    try {
      const { status, token } = await getSingleSignInStatus();

      if (status.isPartnerLoginAllowed) {
        this._ssiToken = token;
        this._ssiAcceptanceRequired = status.displayTos;
      }
    } catch (error) {
      this._ssiToken = null;
      this._ssiAcceptanceRequired = false;
    }

    this.tag('Left').visible = true;
    this.tag('Right').visible = true;
    this.tag('Spinner').visible = false;

    const shouldDisplaySSI = Boolean(this._ssiToken);
    this._views = [shouldDisplaySSI ? 'SSI' : '', 'Mobile', 'Desktop'].filter(Boolean);
    this.tag('Left').buttons = [
      shouldDisplaySSI && {
        label: t('pairing.token.magenta.button'),
        iconLeft: 'Television',
        iconRight: 'ArrowRight',
      },
      {
        label: t('pairing.token.mobile.button'),
        iconLeft: 'QRCode',
        iconRight: 'ArrowRight',
      },
      {
        label: t('pairing.token.computer.button'),
        iconLeft: 'Laptop',
        iconRight: 'ArrowRight',
      },
    ].filter(Boolean);
    this.tag(this._views[0]).visible = true;

    // OTP pairing
    this._requestNewPairingCode();
    this._pairingIntervalId = setInterval(() => Store.dispatch(getStatus()), this._pollingInterval);

    this._setState('Left');
  }

  _inactive() {
    this._clearTokenTimeout();
    this._clearPairingInterval();
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    Store.dispatch(clearPairingToken());
  }

  _switchViews(index) {
    this._views.forEach((view, i) => {
      this.tag(view).visible = i === index;
    });
  }

  async _handleAccept() {
    this._ssiCompletion = true;

    try {
      if (this._ssiAcceptanceRequired) {
        navigateTo('ssi');
      } else {
        await exchangePlatformToken(this._ssiToken);
        await Store.dispatch(getCurrentPairedUser(true));
        this._completePairing();
      }
    } catch (error) {
      Store.dispatch(
        showPopupAction(FATAL_ERROR_POPUP_ID, {
          routeBackOnClose: false,
          exitAppOnClose: false,
        }),
      );
      this.tag('SSI').reset();
    }

    this._ssiCompletion = false;
  }

  _requestNewPairingCode() {
    Store.dispatch(fetchToken());
    Store.dispatch(disableProfileAutoSelect());
  }

  _clearTokenTimeout() {
    if (this._tokenTimeout) {
      clearTimeout(this._tokenTimeout);
    }
  }

  _clearPairingInterval() {
    if (this._pairingIntervalId) {
      clearInterval(this._pairingIntervalId);
    }
    this._pairingIntervalId = null;
  }

  _completePairing() {
    Store.dispatch(clearCache());
    Store.dispatch(loadNavigation());

    // eslint-disable-next-line no-console
    console.log(`[Pairing] Moving to deeplink: ${this._deeplink}`);
    navigateTo(this._deeplink || HOME_PATH, false);
  }

  set pollingInterval(value) {
    this._pollingInterval = value;

    if (this._pairingIntervalId) {
      this._clearPairingInterval();
      this._pairingIntervalId = setInterval(() => Store.dispatch(getStatus()), this._pollingInterval);
    }
  }

  set deeplink(value) {
    this._deeplink = value;
    // eslint-disable-next-line no-console
    console.log(`[Pairing] Deeplink set: ${this._deeplink}`);
  }

  set token(value) {
    if (!value) return;

    this._clearTokenTimeout();
    this._tokenTimeout = setTimeout(() => {
      sendEvent({ key: 'accountPairing' });
      Router.back();
    }, pairing.pairingTimeout);
    this.tag('Desktop').token = value;
  }

  set url(value) {
    this.tag('Mobile').url = value;
  }

  set pairingStatus(status) {
    // preventing race condition when both auth methods where triggered
    if (this._ssiCompletion) {
      return;
    }

    if (status === PAIRING_STATUS_PAIRED) {
      sendEvent({ key: 'deviceConnected' });
      setPairingMethod(PAIRING_METHOD.PAIRING);

      this._completePairing();
    }
  }

  _handleBack() {
    sendEvent({ key: 'pressTokenBackButton' });

    return false;
  }
}
